import { Box, Typography, useTheme } from '@mui/material';
import { TicketDiscount } from 'iconsax-react';
import { type FC, useRef } from 'react';

import { useReconcileDiscounts } from '@core/hooks/useReconcileDiscounts';
import type Enums from '@core/types/api/Enums';
import type { QuoteProductDiscount } from '@core/types/api/QuoteProductDiscount/QuoteProductDiscount';
import {
  discountButtonLabel,
  discountPicksKey,
  grossPriceOf,
  hasSameAppliedDiscounts,
  settledNetPrice,
} from '@core/utils/discounts';
import { hasValue } from '@core/utils/equality';
import { useMatchingDiscounts } from '@web/hooks/useMatchingDiscounts';

import {
  ButtonWithPopover,
  type ButtonWithPopoverHandleRef,
} from '../ButtonWithPopover/ButtonWithPopover';
import { QuoteProductDiscountPopup } from './QuoteProductDiscountPopup';

type Props = {
  companyID: string | null;
  productID: string;
  deliveryType: Enums.ProductDeliveryType | null;
  netUnitPrice: number;
  applied: QuoteProductDiscount[];
  isPriceSettled: boolean;
  onChange: (applied: QuoteProductDiscount[], netUnitPrice: number) => void;
  currencySymbol?: string;
  unitLabel?: string;
  readOnly?: boolean;
};

export const QuoteProductDiscountPicker: FC<Props> = ({
  companyID,
  productID,
  deliveryType,
  netUnitPrice,
  applied,
  isPriceSettled,
  onChange,
  currencySymbol = '$',
  unitLabel = '',
  readOnly = false,
}) => {
  const theme = useTheme();
  const popoverRef = useRef<ButtonWithPopoverHandleRef>(null);
  const { discounts, isSuccess, isError } = useMatchingDiscounts({
    companyID,
    productID,
    deliveryType,
  });
  const grossPrice = grossPriceOf(applied, netUnitPrice);
  const { unappliedNames, unofferedNames, unverifiedNames, clearUnappliedNames } =
    useReconcileDiscounts({
      companyID,
      productID,
      deliveryType,
      netUnitPrice,
      applied,
      matching: discounts,
      isSuccess,
      isError,
      readOnly,
      isPriceSettled,
      onChange,
    });

  const hasSomethingToShow =
    discounts.length > 0 ||
    applied.length > 0 ||
    unappliedNames.length > 0 ||
    unofferedNames.length > 0 ||
    unverifiedNames.length > 0;
  if (!hasValue(companyID) || !hasSomethingToShow) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignSelf: 'flex-start' }}>
      {(discounts.length > 0 || applied.length > 0) && (
        <ButtonWithPopover
          handle={popoverRef}
          title={discountButtonLabel(applied.length)}
          variant='outlined'
          startIcon={<TicketDiscount variant='TwoTone' />}
          sx={{
            alignSelf: 'flex-start',
            marginY: 1,
            padding: '0.875rem 1rem',
            gap: '0.75rem',
            borderRadius: '0.5rem',
            fontSize: '1rem',
            fontWeight: 400,
            textTransform: 'uppercase',
            color: theme.palette.SlabGreen[600],
            borderColor: theme.palette.SlabGreen[600],
            backgroundColor: theme.palette.SlabGreen[50],
            '&:hover': {
              borderColor: theme.palette.SlabGreen[600],
              backgroundColor: theme.palette.SlabGreen[100],
            },
          }}
        >
          <QuoteProductDiscountPopup
            key={discountPicksKey(applied)}
            discounts={discounts}
            applied={applied}
            grossPrice={grossPrice}
            currencySymbol={currencySymbol}
            unitLabel={unitLabel}
            readOnly={readOnly}
            onCancel={(): void => {
              clearUnappliedNames();
              popoverRef.current?.close();
            }}
            onApply={(selected): void => {
              if (!hasSameAppliedDiscounts(selected, applied)) {
                onChange(selected, settledNetPrice(selected, grossPrice));
              }
              clearUnappliedNames();
              popoverRef.current?.close();
            }}
          />
        </ButtonWithPopover>
      )}
      {unappliedNames.length > 0 && (
        <Typography sx={{ fontSize: '0.875rem', color: theme.palette.SlabRed[600] }}>
          {`Removed: ${unappliedNames.join(', ')}. Net price would drop below zero.`}
        </Typography>
      )}
      {unofferedNames.length > 0 && (
        <Typography sx={{ fontSize: '0.875rem', color: theme.palette.SlabRed[600] }}>
          {`Removed: ${unofferedNames.join(', ')}. No longer available for this line.`}
        </Typography>
      )}
      {unverifiedNames.length > 0 && (
        <Typography sx={{ fontSize: '0.875rem', color: theme.palette.SlabRed[600] }}>
          {`Could not verify: ${unverifiedNames.join(', ')}. Check the connection before saving.`}
        </Typography>
      )}
      {!isPriceSettled && applied.length > 0 && (
        <Typography sx={{ fontSize: '0.875rem', color: theme.palette.text.secondary }}>
          Checking buyer pricing…
        </Typography>
      )}
    </Box>
  );
};
